/** @format */

import { useCallback, useEffect, useState } from "react";
import { Brand, ImportJob } from "@question-bank/shared";
import { getImport } from "../api/imports";

/** How often a pending or running job is reloaded. */
const JOB_POLL_MS = 5_000;

interface UseImportJobReturn {
	job: ImportJob | null;
	isLoading: boolean;
	error: string | null;
	reload: () => void;
}

/**
 * Loads one import job. Reloads it every 5 seconds while it is pending or running.
 */

export const useImportJob = (brand: Brand, jobId: string): UseImportJobReturn => {
	const [job, setJob] = useState<ImportJob | null>(null);
	const [isLoading, setIsLoading] = useState(true);
	const [error, setError] = useState<string | null>(null);
	const [reloadToken, setReloadToken] = useState(0);

	const reload = useCallback(() => setReloadToken((token) => token + 1), []);

	useEffect(() => {
		let isCancelled = false;
		setIsLoading(true);
		setError(null);
		setJob(null);

		getImport(brand, jobId)
			.then((result) => {
				if (!isCancelled) {
					setJob(result);
				}
			})
			.catch((err) => {
				if (!isCancelled) {
					setError(err instanceof Error ? err.message : "failed to load import job");
				}
			})
			.finally(() => {
				if (!isCancelled) {
					setIsLoading(false);
				}
			});

		return () => {
			isCancelled = true;
		};
	}, [brand, jobId, reloadToken]);

	useEffect(() => {
		if (job?.status !== "pending" && job?.status !== "running") {
			return;
		}
		let isCancelled = false;
		const timer = setInterval(() => {
			getImport(brand, jobId)
				.then((result) => {
					if (!isCancelled) {
						setJob(result);
					}
				})
				.catch(() => {
					// Keep the job already on screen. The next poll tries again.
				});
		}, JOB_POLL_MS);
		return () => {
			isCancelled = true;
			clearInterval(timer);
		};
	}, [brand, jobId, job?.status]);

	return { job, isLoading, error, reload };
};
